import { ROOT_NODE, useNode, type Node, type NodeHelpersType } from '@craftjs/core';
import { InspectorImage } from '@/components/inspector/InspectorImage/InspectorImage';
import { getVisibilityClasses, DEFAULT_VISIBILITY } from '@builder4/shared/utils/styleHelpers';
import NodeImagePlaceholder from '@/components/userComponents/Image/NodeImagePlaceholder';
import { Resizer } from '../Resizer';
import { useRenderContext } from '@builder4/shared/context/RenderContext';
import clsx from 'clsx';
import SharedImage, {
  defaultProps,
  type ImageProps,
  getImageNodeStylesObject,
} from '@builder4/shared/components/Image';
import { getBreakpointProp } from '@builder4/shared/utils/breakpointUtils';
import type { ContainerProps } from '@builder4/shared/components/Container';
import { IMAGE_NODE_NAME } from '@/utils/constants';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { useAppSelector } from '@/store/hooks';
import { selectEffectiveBreakpoint } from '@/store/system/selectors';
import { removeUndefined } from '@/utils/helperFunc';

// Wrapper component that provides the Resizer and Craft.js integration to the shared Image
export const Image = (props: Partial<ImageProps>) => {
  const { environment } = useRenderContext();
  const previewMode = environment === 'preview';

  props = {
    ...defaultProps,
    ...props,
  };

  const breakpoint = useAppSelector(selectEffectiveBreakpoint);
  const { getBPProp } = useBreakpointAwareProp(IMAGE_NODE_NAME);
  const visibility = getBPProp(props, 'visibility') ?? DEFAULT_VISIBILITY;
  const fillSpace = getBPProp(props, 'fillSpace');
  const src = getBPProp(props, 'src');
  const maxWidth = getBPProp(props, 'maxWidth');
  const {
    connectors: { connect, drag },
  } = useNode();

  // Use the shared imageNodeStyles function for consistent styling
  const visibilityClasses = getVisibilityClasses(visibility);

  const { mobile, tablet, ...computedStyles } = getImageNodeStylesObject(props);
  let currentBpFlexStyle: object = computedStyles;
  if (breakpoint === 'mobile' && mobile) {
    currentBpFlexStyle = { ...computedStyles, ...removeUndefined(mobile) };
  } else if (breakpoint === 'tablet' && tablet) {
    currentBpFlexStyle = { ...computedStyles, ...removeUndefined(tablet) };
  }

  if (previewMode && !src) return null;

  return (
    <Resizer
      className={clsx('image-node pointer-events-none', visibilityClasses, {
        'flex-shrink-1-imp': fillSpace === 'yes',
      })}
      propKey={{ width: 'width', height: 'height' }}
      onRef={(ref) => {
        if (ref) {
          connect(drag(ref));
        }
      }}
      maxWidth={maxWidth}
      style={currentBpFlexStyle}
    >
      <SharedImage {...props}>{!src && <NodeImagePlaceholder />}</SharedImage>
    </Resizer>
  );
};

Image.craft = {
  ...SharedImage.craft,
  related: {
    toolbar: InspectorImage,
  },
  rules: {
    canDrag: (node: Node, helpers: NodeHelpersType) => {
      if (node.data.custom.isLocked) return false;
      const breakpoint = helpers(ROOT_NODE).get().data.props.breakpoint || 'desktop';
      const position = getBreakpointProp(
        node.data.props as Partial<ContainerProps>,
        'position',
        breakpoint,
      );

      return position === 'relative' || position === 'sticky';
    },
  },
};

export default Image;
